import React from 'react';

import { gql } from '@apollo/client';
import { AutocompleteRenderGroupParams, ListSubheader } from '@mui/material';

import {
  BrighthireGuideOptionFragment,
  InterviewGuideType,
  useBrighthireFormsQuery,
} from 'src/generated/mloop-graphql';

import { EMPTY_ARRAY } from 'src/constants';

import { useIsBrighthireIntegrationEnabled } from './useIsBrighthireIntegrationEnabled';

// Helper function to render the group header
export const renderGroup = (params: AutocompleteRenderGroupParams) => [
  params.group === '' ? null : <ListSubheader key={params.key}>{params.group}</ListSubheader>,
  params.children,
];

// Helper function to group the options
export const groupBy = (jobId: string, option: BrighthireGuideOptionFragment) => {
  if (option.id === null) {
    return '';
  }

  if (option.job?.id === jobId) {
    return 'Guides for this job';
  }

  return 'Guides for all jobs';
};

// Main hook that fetches brighthire guide options
export const useBrighthireFormOptions = ({ jobId }) => {
  const isBrighthireEnabled = useIsBrighthireIntegrationEnabled();
  const { data, loading } = useBrighthireFormsQuery({
    variables: {
      jobId,
    },
    skip: !isBrighthireEnabled,
  });

  const options = data?.job?.interviewGuides ?? EMPTY_ARRAY;

  return {
    brighthireEnabled: isBrighthireEnabled,
    loading,
    options: isBrighthireEnabled
      ? [
          {
            id: null,
            name: 'No interview guide',
            remoteInterviewGuideId: '',
            remoteInterviewGuideType: InterviewGuideType.Brighthire,
          },
          ...options,
        ]
      : EMPTY_ARRAY,
  };
};

const BRIGHTHIRE_GUIDE_FRAGMENT = gql`
  fragment BrighthireGuideOption on InterviewGuide {
    id
    name
    remoteInterviewGuideId
    remoteInterviewGuideType
    job {
      id
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BRIGHTHIRE_FORMS_QUERY = gql`
  ${BRIGHTHIRE_GUIDE_FRAGMENT}
  query BrighthireForms($jobId: uuid!) {
    job(id: $jobId) {
      id
      interviewGuides {
        ...BrighthireGuideOption
      }
    }
  }
`;
